import { useLeadLiveUpdates } from 'features/lead/live-updates';
import { ErrorBanner } from 'shared/ui/error-banner';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useMemo } from 'react';
import { Card, Paper, Stack } from '@mui/material';
import { LeadPropsForm } from 'features/lead/lead-props-form/lead-props-form';
import { useTitleHook } from 'hooks/page-title.hook';
import { useIdParam } from 'utils/url-paramters.hook';
import { PageHeader } from 'shared/ui/details-page-header';
import { formatDateTime } from 'utils/date-format-helpers';
import { LeadContentWidget } from 'widgets/lead/lead-content';
import { LeadSubheader } from 'entities/lead/lead-subheader';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { LeadMediaPlan } from 'features/lead/lead-mediaplan/lead-mediaplan';
import { TabInfo, Tabs } from 'shared/ui/tabs';
import { LeadHeaderActions } from 'features/lead/lead-header/lead-header-actions';
import { useLeadDetails } from 'features/lead/api';
import { ContactsPanel } from 'widgets/contacts/contact-panel';
import { EntitiesWithContacts } from 'features/contact/contacts.types';
import { LeadCompanies } from 'features/lead/lead-companies';
import { LeadComments } from 'features/lead/lead-comments';
import { LeadInvoices } from './tabs/lead-invoices';
import { LeadEvent } from 'features/lead/lead-event/lead-event';
import { AuditEntityType } from '@sr/dto';
import { AuditLogSection } from 'entities/audit/ui/audit-log-section';

export const LeadDetailsPage = () => {
  const leadId = useIdParam('leadId');

  const { data: lead, error } = useLeadDetails(leadId);
  useLeadLiveUpdates(lead && !error ? leadId : undefined);
  useTitleHook(lead && `Лид ${leadId} от ${formatDateTime(lead.createdAt)}`);

  const tabs: TabInfo[] = useMemo(
    () =>
      lead
        ? [
          {
            label: 'Лид',
            // icon: ReactElement;
            component: <LeadPropsForm lead={lead} />
          },
          {
            label: 'Медиаплан',
            // icon: ReactElement;
            component: <LeadMediaPlan lead={lead} />
          },
          {
            label: 'Связи',
            // icon: ReactElement;
            component: <LeadCompanies leadId={leadId} />
          },
          {
            label: 'Дела',
            // icon: ReactElement;
            component: <LeadEvent lead={lead} />
          },
          {
            label: 'Счета',
            // icon: ReactElement;
            component: <LeadInvoices leadId={leadId}/>
          },
          {
            label: 'Аудит',
            component: <AuditLogSection entityType={AuditEntityType.LEAD} entityId={leadId} />
          }
        ]
        : [],
    [lead, leadId]
  );

  if (error) return <ErrorBanner errorMessage={extractNestJsErrorMessage(error)} />;
  if (!lead) return <LoadingBanner />;

  return (
    <>
      <PageHeader
        title={
          <PageHeader.Text>
            Лид № {lead.id}: {lead.subject}
          </PageHeader.Text>
        }
        subHeader={<LeadSubheader lead={lead} />}
        actions={
          <>
            <LeadHeaderActions lead={lead} />
          </>
        }
      />

      <Stack direction="row" spacing={2}>
        <Stack sx={{ flex: '1 1 0', minWidth: 0 }} direction="column">
          <Card sx={{ mb: 2, p: 1 }}>
            <ContactsPanel relationId={leadId} relationType={EntitiesWithContacts.lead} />
          </Card>
          <LeadContentWidget lead={lead} />
        </Stack>
        <Stack sx={{ flex: '1 1 0', minWidth: 0 }}>
          <Paper sx={{ p: 1, minWidth: 0 }}>
            <Tabs tabs={tabs} />
          </Paper>
          <LeadComments id={leadId} />
        </Stack>
      </Stack>
    </>
  );
};

export default LeadDetailsPage;
